<template>
  <div>
    <BasePaymentRequestsTable
      :query="query"
      :additional-query-variables="{ hires: [props.hireId] }"
      :filter-black-list="['accounts', 'hires', 'hireOwners']"
      :initial-order="initialOrder"
      :quick-filters="['statuses']"
      :search-placeholder="$t('Search ID, PO number, invoice...')"
      data-test="client-payment-requests-for-hire-table"
    >
      <template #action="{ row }">
        <PaymentRequestActionColumn :row="row" :simple-mode="true" />
      </template>
      <template #no-results="slotProps">
        <div class="tw-w-full tw-flex tw-justify-center tw-py-10 tw-px-4">
          <div class="tw-w-full tw-max-w-2xl">
            <WdsStack
              v-if="slotProps?.loading || hireDetailsLoading"
              direction-row
              align-center
              gap-sm
            >
              <WdsSpinner size="md" />
              <WdsText>{{ $t('Loading payment requests...') }}</WdsText>
            </WdsStack>
            <WdsStack v-else justify-space-between align-center direction-column gap-sm>
              <WdsStack direction-row gap-sm>
                <WdsStack direction-col gap-xs>
                  <WdsText size="lg" weight="semibold" secondary>
                    {{ $t('No pending requests.') }}
                    <span v-if="canSendReminder">{{ $t('Awaiting one?') }}</span>
                  </WdsText>
                </WdsStack>
              </WdsStack>
              <WdsStack v-if="canSendReminder" direction-col align-end gap-xs>
                <WdsButton
                  size="md"
                  :disabled="!canSendReminder"
                  data-test="hire-payment-requests-send-reminder"
                  @click="openReminderModal"
                >
                  <WdsStack direction-row align-center gap-xs>
                    <span>{{ $t('Send reminder') }}</span>
                    <WdsIcon name="send-alt" :size="16" />
                  </WdsStack>
                </WdsButton>
                <WdsText v-if="!canSendReminder" size="sm" color="gray-500">
                  {{ $t('You do not have permission to send reminders for this hire.') }}
                </WdsText>
              </WdsStack>
            </WdsStack>
          </div>
        </div>
      </template>
    </BasePaymentRequestsTable>

    <ModalRemindToBill
      v-if="reminderModalOpen && hireNumber"
      :hire-ids="[hireNumber]"
      :job-name="hireJobName"
      :freelancer-name="hireWorkerName"
      :default-message="hireDefaultReminderMessage || undefined"
      :is-open="reminderModalOpen"
      @closed="closeReminderModal"
      @reminder-sent="handleReminderSent"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsButton, WdsIcon, WdsSpinner, WdsStack, WdsText } from '@worksome/wds-components'
import { TypedQueryDocumentNode } from 'graphql/index'
import BasePaymentRequestsTable from './BasePaymentRequestsTable.vue'
import { graphql } from '@/gql'
import { PaymentRequestOrderByColumn, SortOrder } from '@/gql/graphql'
import PaymentRequestActionColumn from '@/components/GraphqlDataTable/columns/PaymentRequest/Client/PaymentRequestActionColumn.vue'
import ModalRemindToBill from '@/components/ModalRemindToBill.vue'
import { useTranslation } from '@/composables/translation'

const props = defineProps<{
  hireId: string
}>()

const { $t } = useTranslation()

const reminderModalOpen = ref(false)

const initialOrder = {
  field: PaymentRequestOrderByColumn.Priority,
  order: SortOrder.Asc,
}

const query = graphql(`
  query ClientPaymentRequestsForHireQuery(
    $accounts: [ID!]
    $hires: [ID!]
    $search: String
    $first: Int!
    $page: Int
    $orderBy: [PaymentRequestOrderByClauseInput!]
    $statuses: [PaymentRequestStatus!]
    $requestTypes: [PaymentRequestType!]
    $workerPayoutStatuses: [PaymentRequestWorkerPayoutStatus!]
    $hasTimesheet: Boolean
    $hasPurchaseOrderNumber: Boolean
    $rateTypes: [RateType!]
    $requestedDateRange: DateRangeInput
    $billingStartDateRange: DateRangeInput
    $billingEndDateRange: DateRangeInput
    $hireOwners: [ID!]
  ) {
    paymentRequests(
      accounts: $accounts
      hires: $hires
      search: $search
      first: $first
      page: $page
      orderBy: $orderBy
      statuses: $statuses
      requestTypes: $requestTypes
      workerPayoutStatuses: $workerPayoutStatuses
      hasTimesheet: $hasTimesheet
      hasPurchaseOrderNumber: $hasPurchaseOrderNumber
      rateTypes: $rateTypes
      requestedDateRange: $requestedDateRange
      billingStartDateRange: $billingStartDateRange
      billingEndDateRange: $billingEndDateRange
      hireOwners: $hireOwners
    ) {
      data {
        ...PaymentRequestRequestedAtColumn
        ...PaymentRequestStatusIdColumn
        ...PaymentRequestPoNumberColumn
        ...PaymentRequestTypeColumn
        ...PaymentRequestDetailsColumn
        ...PaymentRequestBillingStartDateColumn
        ...PaymentRequestBillingEndDateColumn
        ...PaymentRequestRateColumnClient
        ...PaymentRequestUnitsColumnClient
        ...PaymentRequestWorkerPayoutStatusColumn
        ...PaymentRequestTotalAmountColumn
        ...PaymentRequestInvoiceColumn
        ...PaymentRequestActionColumn
      }
    }
  }
`) as TypedQueryDocumentNode

const { result: hireDetailsResult, loading: hireDetailsLoading } = useQuery(
  graphql(`
    query PaymentRequestsForHireDetails($hire: ID!) {
      hire(id: $hire) {
        id
        number
        worker {
          id
          name
        }
        job {
          id
          name
        }
        canRemindWorkerForBilling
        company {
          id
          settings {
            defaultMessageRemindWorkerToBill
          }
        }
      }
    }
  `),
  {
    hire: props.hireId,
  },
  {
    clientId: 'publicApiCustomFields',
  },
)

const hireDetails = computed(() => hireDetailsResult.value?.hire)
const hireNumber = computed(() => hireDetails.value?.number ?? '')
const hireWorkerName = computed(() => hireDetails.value?.worker?.name ?? '')
const hireJobName = computed(() => hireDetails.value?.job?.name ?? '')
const hireDefaultReminderMessage = computed(
  () => hireDetails.value?.company?.settings?.defaultMessageRemindWorkerToBill ?? '',
)

const canSendReminder = computed(
  () => !!hireDetails.value?.canRemindWorkerForBilling && !!hireNumber.value,
)

const openReminderModal = () => {
  if (!canSendReminder.value) {
    return
  }

  reminderModalOpen.value = true
}

const closeReminderModal = () => {
  reminderModalOpen.value = false
}

const handleReminderSent = () => {
  closeReminderModal()
}
</script>
